/**
 * DeliveryShipmentDetail - Right-side detail panel for a selected shipment
 *
 * Enhanced with live tracking:
 *  - When moving to "In Transit" → requests browser geolocation, sends departure ping
 *  - When moving to "Delivered"  → requests browser geolocation, sends arrival ping
 *  - Shows DeliveryTrackingMap with the route polyline + ETA once tracking data exists
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Tag, Truck, CheckCircle2, Package, MapPin, User,
  Calendar, Hash, Box, ArrowRight, Archive, Printer,
  Navigation, Clock, AlertTriangle, Loader, Map, Image
} from 'lucide-react';
import { DELIVERY_STATUSES, DELIVERY_STATUS_COLORS } from '../manifest';
import DeliveryTrackingMap from './DeliveryTrackingMap';
import api from '../../../lib/api';

// ── Geolocation helper ────────────────────────────────────────────────────────
function requestLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(new Error(err.message || 'Location access denied.')),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

// ── Google Maps loader ─────────────────────────────────────────────────────────
let googleMapsLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google && window.google.maps) return Promise.resolve();
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;
  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const keyParam = apiKey ? `&key=${apiKey}` : '';
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=places${keyParam}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

// ── Geocoding helper ──────────────────────────────────────────────────────────
async function geocodeAddress(address) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  // Ensure Google Maps is loaded
  await loadGoogleMaps(apiKey);
  
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
      reject(new Error('Google Maps API not loaded'));
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const location = results[0].geometry.location;
        resolve({ lat: location.lat(), lng: location.lng() });
      } else {
        reject(new Error(`Geocoding failed: ${status}`));
      }
    });
  });
}

// ── Distance calculation (Haversine formula) ──────────────────────────────────
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function DeliveryShipmentDetail({
  shipment: s,
  isDark,
  onClose,
  onStatusChange,
  onArchive,
  onPrintLabels,
  updatingId,
}) {
  if (!s) return null;

  const colors = DELIVERY_STATUS_COLORS[s.status] || DELIVERY_STATUS_COLORS['Imported'];
  const isUpdating = updatingId === s.id;

  const nextStatus = {
    'Imported':       'Labels Printed',
    'Labels Printed': 'In Transit',
    'In Transit':     'Delivered',
    'Delivered':      null,
  }[s.status];

  // ── Tracking state ──────────────────────────────────────────────────────────
  const [route, setRoute] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [locationState, setLocationState] = useState('idle');
  // idle | requesting | sending | error | success
  const [locationError, setLocationError] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [lastPingId, setLastPingId] = useState(null); // Store ping ID after location capture
  const [lightboxImage, setLightboxImage] = useState(null); // For viewing images
  const [uploadingImage, setUploadingImage] = useState(false);
  const [geofenceStatus, setGeofenceStatus] = useState(null); // { inside: boolean, distance: number }

  // Load existing route data whenever shipment changes
  const loadRoute = useCallback(async () => {
    if (!s?.id) return;
    // Only fetch if the shipment is In Transit or Delivered
    if (s.status !== 'In Transit' && s.status !== 'Delivered') {
      setRoute(null);
      return;
    }
    try {
      setRouteLoading(true);
      const data = await api.get(`/api/delivery/shipments/${s.id}/route`);
      setRoute(data || null);
    } catch (err) {
      // 404 is fine – no route yet
      if (err.status !== 404) console.error('Failed to load delivery route:', err);
      setRoute(null);
    } finally {
      setRouteLoading(false);
    }
  }, [s?.id, s?.status]);

  useEffect(() => {
    loadRoute();
  }, [loadRoute]);

  // ── Image handling ──────────────────────────────────────────────────────────
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setLocationError('Please select an image file');
        return;
      }
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Upload image to existing ping
  const handleImageUploadToPing = async () => {
    if (!selectedImage || !lastPingId) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);

      const response = await api.upload(`/api/delivery/shipments/${s.id}/ping/${lastPingId}/image`, formData);
      
      console.log('[Delivery Tracking] Image uploaded to ping:', response);
      
      // Clear image and reload route to get updated image URL
      clearImage();
      await loadRoute();
      setLastPingId(null);
    } catch (err) {
      console.error('[Delivery Tracking] Failed to upload image:', err);
      setLocationError('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  // ── Status change with location capture ────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    const needsLocation = newStatus === 'In Transit' || newStatus === 'Delivered';

    if (!needsLocation) {
      // No location needed for Imported → Labels Printed
      onStatusChange(newStatus);
      return;
    }

    setLocationError(null);
    setLocationState('requesting');

    let coords;
    try {
      coords = await requestLocation();
    } catch (err) {
      setLocationState('error');
      setLocationError(err.message);
      return;
    }

    setLocationState('sending');

    try {
      // For "Delivered" status, check geofence (500ft = 152.4 meters)
      let geofenceCheck = null;
      if (newStatus === 'Delivered' && s.customer_address) {
        try {
          const deliveryCoords = await geocodeAddress(s.customer_address);
          const distanceMeters = calculateDistance(coords.lat, coords.lng, deliveryCoords.lat, deliveryCoords.lng);
          const distanceFeet = distanceMeters * 3.28084;
          const isInside = distanceMeters <= 152.4; // 500ft in meters
          
          geofenceCheck = {
            inside: isInside,
            distanceMeters,
            distanceFeet,
            deliveryLat: deliveryCoords.lat,
            deliveryLng: deliveryCoords.lng
          };
          
          setGeofenceStatus(geofenceCheck);
          console.log('[Delivery Tracking] Geofence check:', geofenceCheck);
        } catch (geocodeErr) {
          console.warn('[Delivery Tracking] Failed to geocode address for geofence check:', geocodeErr);
          // Continue without geofence check
        }
      }

      // 1. Send the location ping to our backend FIRST (before status update)
      // This ensures the location is saved even if status update fails
      const formData = new FormData();
      formData.append('trigger_status', newStatus === 'In Transit' ? 'in_transit' : 'delivered');
      formData.append('lat', coords.lat);
      formData.append('lng', coords.lng);
      formData.append('accuracy_meters', coords.accuracy);
      formData.append('device_type', /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop');
      formData.append('user_agent', navigator.userAgent);
      
      // Add geofence data if available
      if (geofenceCheck) {
        formData.append('geofence_inside', geofenceCheck.inside.toString());
        formData.append('geofence_distance_meters', geofenceCheck.distanceMeters.toString());
        formData.append('delivery_lat', geofenceCheck.deliveryLat.toString());
        formData.append('delivery_lng', geofenceCheck.deliveryLng.toString());
      }
      
      // Add image if selected
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      // Use upload method for FormData (handles multipart/form-data correctly)
      const pingResponse = await api.upload(`/api/delivery/shipments/${s.id}/ping`, formData);

      console.log('[Delivery Tracking] Location ping sent successfully:', pingResponse);

      // Store ping ID for later image upload
      if (pingResponse?.pingId) {
        setLastPingId(pingResponse.pingId);
      }

      // Clear image after successful upload (if it was sent with ping)
      if (selectedImage) {
        clearImage();
      }

      // 2. Update the shipment status (this also updates the UI)
      onStatusChange(newStatus);

      // 3. Use route data from ping response (avoids race condition) or reload if not provided
      if (pingResponse?.route) {
        setRoute(pingResponse.route);
        console.log('[Delivery Tracking] Route data received from ping response');
      } else {
        // Fallback: reload route data if not in response
        await loadRoute();
      }
      
      // Set to success state to show image upload option
      setLocationState('success');
      
      // Don't auto-reset - let user attach image if they want
      // State will reset when component unmounts or status changes
    } catch (err) {
      console.error('[Delivery Tracking] Failed to send location ping:', err);
      setLocationState('error');
      const errorMsg = err?.data?.message || err?.message || 'Unknown error';
      setLocationError(`Location was captured but failed to save: ${errorMsg}. Please check the console for details.`);
    }
  };

  // ── Field renderer ──────────────────────────────────────────────────────────
  const field = (label, value) => {
    if (!value && value !== 0) return null;
    return (
      <div className={`flex items-start justify-between py-2.5 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
        <span className={`text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</span>
        <span className={`text-xs text-right max-w-[55%] ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{String(value)}</span>
      </div>
    );
  };

  // ── Location status banner ──────────────────────────────────────────────────
  const LocationBanner = () => {
    if (locationState === 'idle' && !locationError) return null;

    if (locationState === 'requesting') {
      return (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border mb-3 ${isDark ? 'bg-amber-900/20 border-amber-700/50 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
          <Navigation size={14} className="animate-pulse flex-shrink-0" />
          <span className="text-xs font-medium">Requesting your location… please allow access in your browser.</span>
        </div>
      );
    }

    if (locationState === 'sending') {
      return (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border mb-3 ${isDark ? 'bg-violet-900/20 border-violet-700/50 text-violet-300' : 'bg-violet-50 border-violet-200 text-violet-700'}`}>
          <Loader size={14} className="animate-spin flex-shrink-0" />
          <span className="text-xs font-medium">Saving location ping & calculating route…</span>
        </div>
      );
    }

    if (locationState === 'success') {
      return (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border mb-3 ${isDark ? 'bg-green-900/20 border-green-700/50 text-green-300' : 'bg-green-50 border-green-200 text-green-700'}`}>
          <CheckCircle2 size={14} className="flex-shrink-0" />
          <span className="text-xs font-medium">Location saved! You can now attach a photo below.</span>
        </div>
      );
    }

    if (locationState === 'error' || locationError) {
      return (
        <div className={`flex items-start gap-2 px-4 py-2.5 rounded-lg border mb-3 ${isDark ? 'bg-red-900/20 border-red-700/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium">Location error</p>
            <p className="text-xs opacity-80 mt-0.5">{locationError}</p>
            <button
              onClick={() => { setLocationState('idle'); setLocationError(null); }}
              className="text-xs underline mt-1 opacity-70 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`w-[420px] flex-shrink-0 flex flex-col border-l overflow-hidden ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className={`flex items-center justify-between px-5 py-4 border-b flex-shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div>
          <h2 className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {s.delivery_no || s.shipment_no || 'Shipment'}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${colors.bg} ${colors.border} ${colors.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
              {s.status}
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
        >
          <X size={18} />
        </button>
      </div>

      {/* ── Scrollable content ─────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-5 py-4">

        {/* Location error / status banner */}
        <LocationBanner />

        {/* ── Status progression ─────────────────────────────────────────── */}
        <div className="mb-5">
          <div className={`text-xs font-medium uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Status
          </div>
          <div className="flex items-center gap-1">
            {DELIVERY_STATUSES.map((st, i) => {
              const stColors = DELIVERY_STATUS_COLORS[st];
              const isActive = st === s.status;
              const isPast = DELIVERY_STATUSES.indexOf(s.status) > i;
              return (
                <React.Fragment key={st}>
                  <div
                    className={`flex-1 text-center py-1.5 rounded text-xs font-medium transition-colors cursor-pointer border ${
                      isActive
                        ? `${stColors.bg} ${stColors.border} ${stColors.text}`
                        : isPast
                          ? isDark ? 'bg-green-900/20 border-green-800 text-green-500' : 'bg-green-50 border-green-200 text-green-600'
                          : isDark ? 'bg-gray-800 border-gray-700 text-gray-600' : 'bg-gray-50 border-gray-200 text-gray-400'
                    }`}
                    onClick={() => !isActive && !isUpdating && handleStatusChange(st)}
                  >
                    {st.split(' ')[0]}
                  </div>
                  {i < DELIVERY_STATUSES.length - 1 && (
                    <ArrowRight size={10} className={isDark ? 'text-gray-700' : 'text-gray-300'} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {nextStatus && (
            <button
              onClick={() => handleStatusChange(nextStatus)}
              disabled={isUpdating || locationState === 'requesting' || locationState === 'sending'}
              className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {(isUpdating || locationState === 'sending') ? (
                <Loader size={14} className="animate-spin" />
              ) : locationState === 'requesting' ? (
                <>
                  <Navigation size={14} className="animate-pulse" />
                  Requesting Location…
                </>
              ) : (
                <>
                  {(nextStatus === 'In Transit' || nextStatus === 'Delivered') && (
                    <Navigation size={14} />
                  )}
                  Move to {nextStatus}
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          )}

          {/* Image upload BEFORE location capture (optional) */}
          {(nextStatus === 'In Transit' || nextStatus === 'Delivered') && locationState === 'idle' && (
            <div className="mt-3 space-y-2">
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Attach Photo
              </label>
              {!imagePreview ? (
                <label className={`block w-full px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  isDark 
                    ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-800 text-gray-300' 
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 text-xs">
                    <Package size={12} />
                    <span>Choose Image</span>
                  </div>
                </label>
              ) : (
                <div className={`relative rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-32 object-cover"
                  />
                  <button
                    onClick={clearImage}
                    className={`absolute top-2 right-2 p-1 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors`}
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Image upload AFTER location capture */}
          {(nextStatus === 'In Transit' || nextStatus === 'Delivered') && locationState === 'success' && lastPingId && (
            <div className="mt-3 space-y-2">
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Attach Photo (Optional)
              </label>
              {!imagePreview ? (
                <label className={`block w-full px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  isDark 
                    ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-800 text-gray-300' 
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 text-xs">
                    <Image size={12} />
                    <span>Choose Image</span>
                  </div>
                </label>
              ) : (
                <div className="space-y-2">
                  <div className={`relative rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-32 object-cover"
                    />
                    <button
                      onClick={clearImage}
                      className={`absolute top-2 right-2 p-1 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <button
                    onClick={handleImageUploadToPing}
                    disabled={uploadingImage}
                    className="w-full flex items-center justify-center gap-2 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader size={12} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Image size={12} />
                        Upload Photo
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Location hint for tracking-enabled transitions */}
          {(nextStatus === 'In Transit' || nextStatus === 'Delivered') && locationState === 'idle' && !locationError && (
            <p className={`text-xs mt-2 text-center ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
              <Navigation size={10} className="inline mr-1 opacity-60" />
              Your location will be recorded when you move to {nextStatus}
            </p>
          )}
        </div>

        {/* ── Live Tracking Map ───────────────────────────────────────────── */}
        {(s.status === 'In Transit' || s.status === 'Delivered') && (
          <div className="mb-5">
            <div className={`flex items-center justify-between mb-2`}>
              <div className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Live Tracking
              </div>
              <button
                onClick={() => setShowMap(v => !v)}
                className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <Map size={12} />
                {showMap ? 'Hide' : 'Show'} Map
              </button>
            </div>

            {routeLoading ? (
              <div className={`flex items-center justify-center rounded-xl border h-24 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <Loader size={18} className="animate-spin text-violet-500" />
              </div>
            ) : showMap ? (
              route && route.geofence_inside === false && route.destination_lat && route.destination_lng && route.delivery_address_lat && route.delivery_address_lng ? (() => {
                // Calculate distance between intended delivery address and actual delivery location
                const intendedLat = parseFloat(route.delivery_address_lat);
                const intendedLng = parseFloat(route.delivery_address_lng);
                const actualLat = parseFloat(route.destination_lat);
                const actualLng = parseFloat(route.destination_lng);
                const distanceMeters = calculateDistance(intendedLat, intendedLng, actualLat, actualLng);
                const distanceMiles = distanceMeters / 1609.34;
                
                return (
                  <div>
                    <div className={`mb-2 px-3 py-2 rounded-lg border ${isDark ? 'border-red-700 bg-red-900/20' : 'border-red-200 bg-red-50'}`}>
                      <div className={`text-xs font-semibold mb-1 flex items-center gap-1.5 ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                        <AlertTriangle size={12} />
                        Delivered to Wrong Location
                      </div>
                      <div className={`text-xs ${isDark ? 'text-red-200' : 'text-red-600'}`}>
                        This shipment was delivered {distanceMiles.toFixed(2)} miles away from the intended delivery address.
                      </div>
                    </div>
                  <DeliveryTrackingMap
                    route={route}
                    isDark={isDark}
                    height="260px"
                    showBothLocations={true}
                    actualLocation={{ lat: parseFloat(route.destination_lat), lng: parseFloat(route.destination_lng) }}
                    intendedLocation={{ lat: parseFloat(route.delivery_address_lat), lng: parseFloat(route.delivery_address_lng) }}
                  />
                  <div className="flex items-center gap-4 mt-2 text-xs px-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Intended Location</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Actual Delivery</span>
                    </div>
                  </div>
                </div>
                );
              })() : (
                <DeliveryTrackingMap
                  route={route}
                  isDark={isDark}
                  height="260px"
                />
              )
            ) : null}

            {/* Tracking stats row */}
            {route && (
              <div className={`mt-2 grid grid-cols-2 gap-2`}>
                {route.estimated_arrival_at && s.status === 'In Transit' && (
                  <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>ETA</div>
                    <div className={`text-sm font-semibold mt-0.5 ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                      {new Date(route.estimated_arrival_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {new Date(route.estimated_arrival_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
                {route.actual_arrival_at && s.status === 'Delivered' && (
                  <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Delivered At</div>
                    <div className={`text-sm font-semibold mt-0.5 ${isDark ? 'text-green-300' : 'text-green-600'}`}>
                      {new Date(route.actual_arrival_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {new Date(route.actual_arrival_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
                {route.estimated_distance_meters && (
                  <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Distance</div>
                    <div className={`text-sm font-semibold mt-0.5 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {(route.estimated_distance_meters / 1609.34).toFixed(1)} mi
                    </div>
                    {route.estimated_duration_seconds && (
                      <div className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        ~{Math.round(route.estimated_duration_seconds / 60)} min drive
                      </div>
                    )}
                  </div>
                )}
                {route.origin_address && (
                  <div className={`col-span-2 rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Departed From</div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{route.origin_address}</div>
                  </div>
                )}

                {/* Image attachments */}
                {(route.departure_image_url_signed || route.arrival_image_url_signed) && (
                  <div className={`col-span-2 rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mb-1.5`}>Photos</div>
                    <div className="flex items-center gap-2">
                      {route.departure_image_url_signed && (
                        <button
                          onClick={() => setLightboxImage({ url: route.departure_image_url_signed, filename: 'Departure Photo' })}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                            isDark 
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          <Image size={12} />
                          <span>Departure</span>
                        </button>
                      )}
                      {route.arrival_image_url_signed && (
                        <button
                          onClick={() => setLightboxImage({ url: route.arrival_image_url_signed, filename: 'Arrival Photo' })}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                            isDark 
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          <Image size={12} />
                          <span>Arrival</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Shipment details ────────────────────────────────────────────── */}
        <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Shipment Details
        </div>
        <div className={`rounded-lg border px-4 mb-5 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          {field('Delivery #', s.delivery_no)}
          {field('Shipment #', s.shipment_no)}
          {field('Sales Order #', s.sales_order_no)}
          {field('Distribution Channel', s.distribution_channel)}
          {field('Date', s.shipment_date ? new Date(s.shipment_date).toLocaleDateString() : null)}
        </div>

        <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Customer & Delivery
        </div>
        <div className={`rounded-lg border px-4 mb-5 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          {field('Customer', s.customer_name)}
          {field('Address', s.customer_address)}
          {field('Stop #', s.stop_no)}
          {field('Carrier', s.carrier_name)}
          {field('Trailer #', s.trailer_no)}
        </div>

        <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Items & Packaging
        </div>
        <div className={`rounded-lg border px-4 mb-5 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          {field('Material / Part', s.material_desc)}
          {field('Category', s.category)}
          {field('Item #', s.item_no)}
          {field('Quantity', s.box_quantity)}
        </div>

        {/* Label status */}
        <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Labels
        </div>
        <div className={`rounded-lg border px-4 mb-5 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          <div className={`flex items-center justify-between py-2.5`}>
            <span className={`text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Status</span>
            <span className={`text-xs font-semibold ${s.labels_printed ? 'text-green-400' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {s.labels_printed ? '✓ Printed' : 'Not printed yet'}
            </span>
          </div>
          {s.labels_printed_at && field('Printed At', new Date(s.labels_printed_at).toLocaleString())}
        </div>
      </div>

      {/* ── Footer actions ──────────────────────────────────────────────────── */}
      <div className={`flex-shrink-0 border-t px-5 py-4 space-y-2 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <button
          onClick={onPrintLabels}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Tag size={15} />
          Generate & Print Labels
        </button>
        <button
          onClick={onArchive}
          className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors border ${
            isDark
              ? 'border-gray-700 text-gray-400 hover:bg-gray-800'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <Archive size={14} />
          Archive Shipment
        </button>
      </div>

      {/* Lightbox for viewing images */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
          <img
            src={lightboxImage.url}
            alt={lightboxImage.filename}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
